import { Button, Grid, Stack, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import Dropzone from 'react-dropzone';
import type { DropzoneRef } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import addImageIconUrl from '@resources/img/create/design-lab-v4/icons/add-image.svg?url';
import Loader from '../../../Common/_components/Loader';
import ItemList from '../../../Common/ItemList/ItemList';
import Dialog from '../../../Common/Dialog/Dialog';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogPaper from '../../../Common/Dialog/DialogPaper';
import SearchBar from '../../../Common/ItemList/SearchBar';
import Image from '../../../Account/Images/Image';
import ImageCardSkeleton from '../../../Account/Images/ImageCardSkeleton';
import type { UserImageStore } from '../../../UserData/_stores/UserImageStore';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ImageUploadQueue from '../../../Account/Images/ImageUploadQueue';
import useImageUploadQueue from '../../../Account/Images/useImageUploadQueue';
import { ACCEPTED_IMAGE_UPLOAD_TYPES, getImageUploadUrl, MAX_IMAGE_UPLOAD_FILE_SIZE } from '../../../Account/Images/imageUploadConfig';
import ImageLibraryGuidelinesContent, { GUIDELINES_CONTENT_ID } from './ImageLibraryGuidelinesContent';
import AccountNoResults from '../../../Account/AccountNoResults';

const IMAGES_PER_PAGE = 20;

const StyledDialogPaper = styled(DialogPaper)(({ theme }) => ({
	height: '90%',

	'&.MuiDialog-paperFullScreen': {
		height: '100%',
	},

	[theme.breakpoints.down('dialog_mobile')]: {
		borderRadius: 0,
		height: '100%',
		margin: 0,
		maxHeight: 'none',
		width: '100%',
	}
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
	boxSizing: 'border-box',
	height: '100%',
	overflow: 'hidden',
	padding: theme.spacing(3, 3, 0, 3),
	marginTop: 0,

	[theme.breakpoints.down('md')]: {
		height: 'auto',
		overflowX: 'hidden',
		overflowY: 'auto',
		padding: theme.spacing(2),
	}
}))

const ImageLibraryLayout = styled(Stack)(({ theme }) => ({
	height: '100%',
	minHeight: 0,
	position: 'relative',

	[theme.breakpoints.down('md')]: {
		height: 'auto',
	},
}))

const ImageLibrarySidebar = styled('div')(({ theme }) => ({
	minWidth: 0,
	width: 'auto',

	[theme.breakpoints.up('md')]: {
		width: 300,
		flexShrink: 0,
		boxSizing: 'border-box',
		height: '100%',
		minHeight: 0,
		overflowX: 'hidden',
		overflowY: 'auto',
		scrollbarGutter: 'stable',
		paddingBottom: theme.spacing(3),
		paddingRight: theme.spacing(1),

		'&::-webkit-scrollbar': {
			width: '10px',
			height: '10px',
		},

		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},

		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0, 0, 0, 0.15)',
			borderRadius: '10px',

			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.25)',
			}
		}
	},
}))

const ImageLibraryResults = styled('div')(({ theme }) => ({
	minWidth: 0,
	flexGrow: 1,

	[theme.breakpoints.up('md')]: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},

	[theme.breakpoints.down('md')]: {
		marginTop: theme.spacing(3),
	}
}))

const ImageResultsScroller = styled('div')(({ theme }) => ({
	[theme.breakpoints.up('md')]: {
		boxSizing: 'border-box',
		flex: '1 1 auto',
		minHeight: 0,
		overflowX: 'hidden',
		overflowY: 'auto',
		paddingBottom: theme.spacing(3),
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(3),
		scrollbarGutter: 'stable',

		'&::-webkit-scrollbar': {
			width: '10px',
			height: '10px',
		},

		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},

		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0, 0, 0, 0.15)',
			borderRadius: '10px',

			'&:hover': {
				backgroundColor: 'rgba(0, 0, 0, 0.25)',
			}
		}
	},

	[theme.breakpoints.down('md')]: {
		//marginRight: theme.spacing(-2),
	}
}))

const ImageResultsGrid = styled(Grid)({
	alignContent: 'flex-start',
	boxSizing: 'border-box',
	width: '100%',
})

const ResultsSearch = styled('div')({
	'& > div': {
		marginBottom: 0,
	},
})

const UploadDropPanel = styled('div')(({ theme }) => ({
	backgroundColor: 'rgba(0, 0, 0, 0.025)',
	border: `1px dashed ${theme.palette.divider}`,
	borderRadius: theme.shape.borderRadius,
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1),
	marginBottom: theme.spacing(2),
	padding: theme.spacing(1.5),
	transition: 'background-color 150ms, border-color 150ms',

	'& .MuiButton-root': {
		alignSelf: 'stretch',
	},

	[theme.breakpoints.down('md')]: {
		backgroundColor: 'transparent',
		border: 0,
		flexDirection: 'row',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
		padding: 0,

		'& .MuiButton-root': {
			flex: '1 1 0',
			minWidth: 0,
		}
	},
}))

const UploadDropCopy = styled(Stack)(({ theme }) => ({
	alignItems: 'center',
	gap: theme.spacing(0.25),
	textAlign: 'center',

	[theme.breakpoints.down('md')]: {
		display: 'none',
	}
}))

const GuidelinesToggleButton = styled(Button)(({ theme }) => ({
	display: 'none',

	[theme.breakpoints.down('md')]: {
		alignSelf: 'stretch',
		display: 'inline-flex',
		flex: '1 1 0',
		justifyContent: 'space-between',
		minWidth: 0,
	},

	'& .MuiButton-endIcon': {
		marginLeft: 'auto',
	}
}))

export interface Props {
	opened: boolean
	onClose?: () => void
	onSelect?: (item: UserImageStore) => void
	onUpload?: () => void
}

export default function ImageLibraryBrowser(props: Props) {
	const [t] = useTranslation('design-lab');
	const theme = useTheme();

	const dropzoneRef = useRef<DropzoneRef>(null);

	const loading = useAppSelector(state => state.get('UIData').get('loaders').get('images'));
	const userLang = useAppSelector(state => state.get('userData').get('prefs').get('lang'));

	const mobileGuidelines = useMediaQuery(theme.breakpoints.down('md'));
	const [search, setSearch] = useState('');
	const [guidelinesOpened, setGuidelinesOpened] = useState(false);
	const listId = useMemo(() => 'design-lab-image-library', []);
	const uploadUrl = useMemo(() => getImageUploadUrl(userLang), [userLang]);
	const imageUploadQueue = useImageUploadQueue({
		listId,
		uploadUrl,
	});

	const openDropzone = useCallback(() => {
		if (!dropzoneRef.current) return
		dropzoneRef.current.open()
	}, [])

	const onGuidelinesToggle = useCallback(() => {
		setGuidelinesOpened(value => !value);
	}, []);

	const loadingGrid = useMemo(() => {
		const listSkeletons = [];

		for (let i = 0; i < IMAGES_PER_PAGE; i++) {
			listSkeletons.push(<ImageCardSkeleton key={i} />);
		}

		return listSkeletons;
	}, []);

	const onSelectImage = useCallback((item: UserImageStore) => {
		props.onSelect?.(item);
		props.onClose?.();
	}, [props.onSelect, props.onClose]);

	const renderItem = useCallback((item: UserImageStore) => {
		const imageId = Number(item.get('id'));

		return <Image
			item={item}
			highlighted={imageUploadQueue.recentUploadedImageIds.has(imageId)}
			onSelect={onSelectImage}
		/>
	}, [imageUploadQueue.recentUploadedImageIds, onSelectImage])

	const renderList = useCallback((items: ReactElement | null, links: ReactElement | null, loading: boolean) => {
		return <>
			<ImageResultsScroller>
				<ImageResultsGrid container spacing={{xs: 1, sm: 2}}>
					<Grid item xs={12}>
						<ResultsSearch>
							<SearchBar
								placeholder={t('Search your images...')}
								value={search}
								onChange={setSearch}
							>
							</SearchBar>
						</ResultsSearch>
					</Grid>
					{imageUploadQueue.uploads.length > 0 ? <Grid item xs={12}>
						<ImageUploadQueue
							uploads={imageUploadQueue.uploads}
							onCancel={imageUploadQueue.onCancelUpload}
							onRetry={imageUploadQueue.onRetryUpload}
							onRemove={imageUploadQueue.onRemoveUpload}
							onClearCompleted={imageUploadQueue.onClearCompletedUploads}
							onClearFailed={imageUploadQueue.onClearFailedUploads}
						/>
					</Grid> : null}
					{loading
						? loadingGrid
						: (items ?? <Grid item xs={12}>
							<AccountNoResults compact />
						</Grid>)
					}
				</ImageResultsGrid>
			</ImageResultsScroller>

			{links}
		</>
	}, [loadingGrid, search, t, imageUploadQueue.uploads, imageUploadQueue.onCancelUpload, imageUploadQueue.onClearCompletedUploads, imageUploadQueue.onClearFailedUploads, imageUploadQueue.onRemoveUpload, imageUploadQueue.onRetryUpload])

	return <>
		<Dialog
			open={props.opened}
			onClose={props.onClose}
			fullWidth={true}
			maxWidth="xl"
			PaperComponent={StyledDialogPaper}
			showCloseButton
			fullScreenOnMobile="md"
			closeButtonSx={{
				backgroundColor: 'common.white',

				'&:hover': {
					backgroundColor: 'common.white',
				}
			}}
		>
			<StyledDialogContent>
				<Dropzone
					ref={dropzoneRef}
					noClick
					noKeyboard
					multiple
					accept={ACCEPTED_IMAGE_UPLOAD_TYPES}
					maxSize={MAX_IMAGE_UPLOAD_FILE_SIZE}
					onDropAccepted={imageUploadQueue.onUploadAccepted}
					onDropRejected={imageUploadQueue.onUploadRejected}
				>
					{({ getRootProps, getInputProps, isDragActive }) => (
						<ImageLibraryLayout
							direction={{
								xs: 'column',
								md: 'row',
							}}
							spacing={1}
							{...getRootProps()}
						>
							<ImageLibrarySidebar>
								<input {...getInputProps({ disabled: false })} />
								<Typography
									variant="h2"
									style={{
										marginBottom: '16px',
									}}
								>{t('Image Library')}</Typography>
								<UploadDropPanel>
									<Button
										variant="contained"
										color="primary"
										disableElevation
										size="large"
										onClick={openDropzone}
										startIcon={<img src={addImageIconUrl} alt={t('Add Image icon')} />}
									>{mobileGuidelines ? t('Upload') : t('Upload image')}</Button>
									<GuidelinesToggleButton
										variant="outlined"
										color="inherit"
										aria-controls={GUIDELINES_CONTENT_ID}
										aria-expanded={guidelinesOpened}
										onClick={onGuidelinesToggle}
										endIcon={<KeyboardArrowDownRoundedIcon
											sx={{
												transform: guidelinesOpened ? 'rotate(180deg)' : 'rotate(0deg)',
												transition: 'transform 200ms',
											}}
										/>}
									>{t('Guidelines')}</GuidelinesToggleButton>
									<UploadDropCopy>
										<Typography variant="body2" fontWeight={700}>
											{t('Drag and drop an image here')}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{t('JPG, PNG, WebP, TIFF, BMP, or GIF, up to 100 MB')}
										</Typography>
									</UploadDropCopy>
								</UploadDropPanel>
								<ImageLibraryGuidelinesContent
									mobileGuidelines={mobileGuidelines}
									opened={guidelinesOpened}
								/>

							</ImageLibrarySidebar>
							<ImageLibraryResults>
								<ItemList
									listId={listId}
									dataType={'images'}
									item={renderItem}
									controlled={true}
									searchQuery={search}
									limit={IMAGES_PER_PAGE}
									render={renderList}
								/>
							</ImageLibraryResults>
							{isDragActive ? <Stack spacing={1} sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								bottom: 0,
								right: 0,
								margin: 0,
								backgroundColor: 'rgba(255, 255, 255, 0.75)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}>
								<Typography variant="h2" component="h3">{t('Drop your files here')}</Typography>
								<SaveAltRoundedIcon
									color="secondary"
									sx={{
										fontSize: '4rem',
									}}
								/>
							</Stack> : null}
						</ImageLibraryLayout>
					)}
				</Dropzone>
			</StyledDialogContent>
			{loading && <Loader noModal />}
		</Dialog>
	</>;
}
